import {COMBINED_KILN_OPTION_VALUE, type EquipmentDto, type EquipmentKpiStats} from '@domain/kpis'
import {formatNumber} from '@hconnect/common/utils'
import {CardBox, Column, DataTable, ListTable} from '@hconnect/uikit/src/lib2'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Box, Grid, IconButton, Theme, Tooltip, Typography, useMediaQuery} from '@mui/material'
import {isNumber} from 'lodash'
import moment from 'moment-timezone'
import React, {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {useDowntimeStatisticsYtmMtmState} from '../hooks'

import {DowntimeStatisticsYtmMtmToggleButton} from './DowntimeStatisticsYtmMtmToggleButton'
import {EquipmentKpiStatsViewDropdown} from './EquipmentKpiStatsViewDropdown'
import {StoppageTime} from './StoppageTime'

type EquipmentKpiStatsViewProps = {
  data: EquipmentKpiStats[]
  totalStopped?: number
  undefinedStoppageCount?: number
  undefinedStoppageHours?: number
  isLoading: boolean
  timezone: string
  selectedEquipmentId?: string
  plantEquipmentsData?: EquipmentDto[]
  onEquipmentChange?: (equipment: string) => void
  equipmentDataSource?: string
}

export const EquipmentKpiStatsView: FC<EquipmentKpiStatsViewProps> = ({
  data,
  isLoading,
  timezone,
  selectedEquipmentId,
  totalStopped,
  undefinedStoppageCount,
  undefinedStoppageHours,
  plantEquipmentsData,
  onEquipmentChange,
  equipmentDataSource
}) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const {language} = i18n
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const {kpiView, setkpiView} = useDowntimeStatisticsYtmMtmState()

  const columns = useMemo<Column<EquipmentKpiStats>[]>(() => {
    const columnCreator = (count: string, hours: string, dataTestId: string) => {
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography
            fontSize={14}
            data-test-id={`DataTable-column-${dataTestId}-count`}
            fontWeight="bold"
          >
            {count}
          </Typography>
          <Typography
            fontSize={14}
            data-test-id={`DataTable-column-${dataTestId}-hours`}
          >{`(${hours} ${t('label.hour')})`}</Typography>
        </Box>
      )
    }

    const baseColumns: Column<EquipmentKpiStats>[] = [
      {
        key: `date${kpiView}`,
        label: t('label.month'),
        customTemplate: (row) =>
          `${isMobile ? `${t('label.month')}: ` : ''}${moment(row.month).format('MM/YY')}`
      },
      {
        key: `calendarHours${kpiView}`,
        label: t('label.calendarHours'),
        customTemplate: (row) =>
          `${isMobile ? `${t('label.calendarHours')}: ` : ''}${formatNumber(
            row.calendarHours,
            language
          )}`
      },
      {
        key: `stoppageCount${kpiView}`,
        label: t('label.stoppageCount'),
        customTemplate: (row) => formatNumber(row.incidentCount + row.circumstanceCount, language)
      },
      ...(selectedEquipmentId !== COMBINED_KILN_OPTION_VALUE
        ? [
            {
              key: `undefinedStoppageCountAndHours${kpiView}`,
              label: t('label.undefinedStoppageCountAndHours'),
              customTemplate: (row: EquipmentKpiStats) => {
                const {undefinedStoppageCount, undefinedStoppageHours} = row
                return columnCreator(
                  formatNumber(undefinedStoppageCount, language),
                  formatNumber(undefinedStoppageHours, language),
                  'undefinedStoppage'
                )
              }
            },
            {
              key: `incidentCountAndHours${kpiView}`,
              label: t('label.incidentCountAndHours'),
              customTemplate: (row: EquipmentKpiStats) => {
                const {incidentCount, incidentHours} = row
                return columnCreator(
                  formatNumber(incidentCount, language),
                  formatNumber(incidentHours, language),
                  'incident'
                )
              }
            },
            {
              key: `circumstanceCountAndHours${kpiView}`,
              label: t('label.circumstanceCountAndHours'),
              customTemplate: (row: EquipmentKpiStats) => {
                const {circumstanceCount, circumstanceHours} = row
                return columnCreator(
                  formatNumber(circumstanceCount, language),
                  formatNumber(circumstanceHours, language),
                  'circumstance'
                )
              }
            }
          ]
        : []),
      {
        key: `unplannedStoppageHours${kpiView}`,
        label: t('label.unplannedStoppage'),
        customTemplate: ({unplannedStoppageHours}) => formatNumber(unplannedStoppageHours, language)
      },
      {
        key: `plannedStoppageHours${kpiView}`,
        label: t('label.plannedStoppage'),
        customTemplate: (row: EquipmentKpiStats) => {
          const {plannedStoppageCount, plannedStoppageHours} = row
          return columnCreator(
            formatNumber(plannedStoppageCount, language),
            formatNumber(plannedStoppageHours, language),
            'plannedStoppage'
          )
        }
      },
      {
        key: `runtimeHours${kpiView}`,
        label: t('label.runtime'),
        customTemplate: ({runtimeHours}) => formatNumber(runtimeHours, language)
      },
      {
        key: `equipmentReliabilityCoefficient${kpiView}`,
        label: t(`actions.${kpiView}_short`) + ' ' + t('label.columns.reliability'),
        customTemplate: ({equipmentReliabilityCoefficient}) =>
          equipmentReliabilityCoefficient
            ? formatNumber(equipmentReliabilityCoefficient * 100, language, 0)
            : '-'
      },
      {
        key: `incidentCoefficient${kpiView}`,
        label: t(`actions.${kpiView}_short`) + ' ' + t('label.columns.incidentCoefficient'),
        customTemplate: ({incidentCoefficient}) =>
          incidentCoefficient ? formatNumber(incidentCoefficient * 100, language, 0) : '-'
      },
      {
        key: `meanTimeBetweenFailures${kpiView}`,
        label: t(`actions.${kpiView}_short`) + ' ' + t('label.columns.mtbf'),
        customTemplate: ({meanTimeBetweenFailures}) =>
          meanTimeBetweenFailures ? formatNumber(meanTimeBetweenFailures, language) : '-'
      }
    ]

    if (selectedEquipmentId === COMBINED_KILN_OPTION_VALUE) {
      baseColumns.push({
        key: `KilnProductionInTonnes${kpiView}`,
        label: t(`actions.${kpiView}_short`) + ' ' + t('label.columns.clinkerProduction'),
        customTemplate: ({productionInTonnes}) =>
          productionInTonnes ? formatNumber(productionInTonnes, language) : '-'
      })
    }

    if (selectedEquipmentId !== COMBINED_KILN_OPTION_VALUE) {
      baseColumns.push({
        key: `meanTimeBetweenIncidents${kpiView}`,
        label: t('label.meanTimeBetweenIncidents'),
        customTemplate: ({meanTimeBetweenIncidents}) =>
          meanTimeBetweenIncidents ? formatNumber(meanTimeBetweenIncidents, language) : '-'
      })
    }

    baseColumns.push({
      key: `equipmentOperatingCoefficient${kpiView}`,
      label: t(`actions.${kpiView}_short`) + ' ' + t('label.columns.processCoefficient'),
      customTemplate: ({equipmentOperatingCoefficient}) =>
        equipmentOperatingCoefficient
          ? formatNumber(equipmentOperatingCoefficient * 100, language, 0)
          : '-'
    })

    return baseColumns
  }, [language, isMobile, t, selectedEquipmentId, kpiView])

  const stoppageTime = (
    <>
      {isNumber(undefinedStoppageHours) && isNumber(undefinedStoppageCount) && (
        <Grid display="flex" alignItems="center" gap={1}>
          <StoppageTime
            stoppedTime={formatNumber(undefinedStoppageHours, language)}
            stoppagesCount={formatNumber(undefinedStoppageCount, language)}
            title={t('label.undefinedStoppageCountAndHours')}
          />
          <Tooltip title={t('label.tooltip.undefinedStoppageTime')}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Grid>
      )}

      {isNumber(totalStopped) && (
        <Grid>
          <StoppageTime
            stoppedTime={formatNumber(totalStopped, language)}
            title={t('label.stoppageTime')}
          />
        </Grid>
      )}
    </>
  )

  return (
    <>
      {isMobile ? (
        <>
          {plantEquipmentsData && (
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                display="flex"
                sx={{
                  flexFlow: {
                    xs: 'row wrap',
                    md: 'row wrap-reverse'
                  }
                }}
                gap={2}
                alignItems="start"
                justifyContent="space-between"
              >
                <EquipmentKpiStatsViewDropdown
                  selectedEquipmentId={selectedEquipmentId ?? ''}
                  onChange={onEquipmentChange}
                  plantEquipmentsData={plantEquipmentsData}
                />
                <Grid
                  item
                  xs={6}
                  sm={6}
                  alignItems="center"
                  justifyContent="right"
                  sx={{
                    pb: 1,
                    pt: 0.5
                  }}
                >
                  <DowntimeStatisticsYtmMtmToggleButton
                    value={kpiView}
                    onChange={setkpiView}
                    timezone={timezone}
                    isMobile={isMobile}
                  />
                </Grid>
                <CardBox sx={{width: '100%'}} mb={2} mt={1}>
                  <Grid
                    sx={{width: '100%'}}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    {stoppageTime}
                  </Grid>
                </CardBox>
              </Grid>
            </Grid>
          )}
          <ListTable data={data} columns={columns} />
        </>
      ) : (
        <CardBox data-test-id="kiln-stats-table-container">
          {plantEquipmentsData && (
            <Grid container>
              <Grid
                item
                sm={6}
                md={6}
                lg={8}
                display="flex"
                mb={2}
                sx={{
                  flexFlow: {
                    xs: 'column',
                    md: 'row wrap'
                  }
                }}
                alignItems="start"
                gap={2}
              >
                <EquipmentKpiStatsViewDropdown
                  selectedEquipmentId={selectedEquipmentId ?? ''}
                  onChange={onEquipmentChange}
                  plantEquipmentsData={plantEquipmentsData}
                />
                <Grid display="flex" alignItems="center">
                  {stoppageTime}
                </Grid>
              </Grid>
              <Grid
                item
                sm={6}
                md={6}
                lg={4}
                alignItems="start"
                display="flex"
                gap={2}
                justifyContent="right"
              >
                <DowntimeStatisticsYtmMtmToggleButton
                  value={kpiView}
                  onChange={setkpiView}
                  timezone={timezone}
                />
              </Grid>
            </Grid>
          )}
          <DataTable<EquipmentKpiStats>
            columns={columns}
            data={data}
            loading={isLoading}
            keyExtractor={(entry) => `${kpiView}-${entry.month}-${equipmentDataSource}`}
          />
        </CardBox>
      )}
    </>
  )
}
